/**
 * Platform Admin Login Page
 * 
 * Elevated security login page for platform administrators
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../types/auth';

export default function AdminLoginPage() {
  const { login, authState } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      setError(null);
      const result = await login({
        email: formData.email,
        password: formData.password,
        userType: 'platform_admin',
        rememberMe: formData.rememberMe
      });

      // Verify the user has platform admin role
      if (result.user.role !== ROLES.PLATFORM_ADMIN) {
        setError('Access denied. Platform administrator privileges required.');
        return;
      }

      // Navigate to admin dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please check your credentials.');
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" 
         style={{ 
           background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
           position: 'relative'
         }}>
      
      {/* Background Pattern */}
      <div className="position-absolute top-0 start-0 w-100 h-100 opacity-10">
        <div className="position-absolute" style={{ top: '10%', left: '10%', width: '100px', height: '100px', background: 'white', borderRadius: '50%' }}></div>
        <div className="position-absolute" style={{ top: '60%', right: '15%', width: '150px', height: '150px', background: 'white', borderRadius: '50%' }}></div>
        <div className="position-absolute" style={{ bottom: '20%', left: '20%', width: '80px', height: '80px', background: 'white', borderRadius: '50%' }}></div>
      </div>

      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5 col-xl-4">
            
            {/* Header */}
            <div className="text-center mb-4">
              <div className="mb-4">
                <div className="bg-white rounded-circle d-inline-flex align-items-center justify-content-center shadow-lg"
                     style={{ width: '80px', height: '80px' }}>
                  <i className="bi bi-shield-lock text-primary" style={{ fontSize: '2.5rem' }}></i>
                </div>
              </div>
              <h2 className="fw-bold text-white mb-2">Platform Admin Portal</h2>
              <p className="text-white-50">
                Secure access for system administrators
              </p>
            </div>

            {/* Login Form */}
            <div className="card border-0 shadow-lg" style={{ borderRadius: '1rem' }}>
              <div className="card-body p-4 p-md-5">
                
                {/* Security Notice */}
                <div className="admin-alert alert-info mb-4">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-shield-exclamation me-2"></i>
                    <small>
                      <strong>Elevated Security:</strong> This portal requires platform administrator privileges.
                    </small>
                  </div>
                </div>

                {/* Development Credentials Notice */}
                <div className="admin-alert alert-warning mb-4">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-info-circle me-2"></i>
                    <small>
                      <strong>Development Mode:</strong> Use <code>platform@admin.com</code> / <code>password123</code> to test login.
                    </small>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Error Display */}
                  {error && (
                    <div className="admin-alert alert-danger mb-4">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <span>{error}</span>
                      </div>
                    </div>
                  )}

                  {/* Email Field */}
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label fw-semibold">
                      <i className="bi bi-envelope me-2"></i>
                      Administrator Email
                    </label>
                    <input
                      type="email"
                      className="form-control form-control-lg"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="platform@admin.com"
                      required
                      autoComplete="email"
                      style={{ borderRadius: '0.5rem' }}
                    />
                  </div>

                  {/* Password Field */}
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label fw-semibold">
                      <i className="bi bi-key me-2"></i>
                      Password
                    </label>
                    <div className="input-group">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="form-control form-control-lg"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Enter your secure password"
                        required
                        autoComplete="current-password"
                        style={{ borderRadius: '0.5rem 0 0 0.5rem' }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ borderRadius: '0 0.5rem 0.5rem 0' }}
                      >
                        <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                      </button>
                    </div>
                  </div>

                  {/* Remember Me */}
                  <div className="mb-4">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="rememberMe"
                        name="rememberMe"
                        checked={formData.rememberMe}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="rememberMe">
                        Keep me signed in for 30 days
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={authState.isLoading}
                    className="btn btn-admin w-100 btn-lg mb-3"
                  >
                    {authState.isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </span>
                        Authenticating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-shield-check me-2"></i>
                        Access Admin Portal
                      </>
                    )}
                  </button>
                </form>

                {/* Security Features */}
                <div className="text-center">
                  <small className="text-muted">
                    <i className="bi bi-shield-lock me-1"></i>
                    Protected by enterprise-grade security
                  </small>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center mt-4">
              <small className="text-white-50">
                Platform Admin Portal v1.0 • 
                <i className="bi bi-shield-check mx-1"></i>
                Secure Access Only
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}